/**
 * AgeTermsGate — the post-sign-in age+terms overlay for a house whose SSR entry gate is parked
 * (plan `thinking/plans/2026_09_17_ovid_veiled_wall.md`, step 7 — ovid.gallery). Where `AgeGate` (`@product/ovid/components/AgeGate`) asks at entry,
 * for every visitor, before any account exists, this asks once, after the first sign-in, and asks
 * both things the entry gate used to ask on its own (age, terms) in one gesture: two independent
 * checkboxes and one "Continue".
 *
 * Deliberately its own component, not `TosConsentCard` (`core/consent/TosConsentCard.tsx`): that
 * card renders exactly one checkbox and no button of its own by design — "the second press is the
 * surface's own CTA" — and there is no separate surface CTA here for the unveil to be. This one
 * owns its own submit affordance and its own focus/scroll-lock behaviour, the same posture as
 * `AgeGate.tsx`.
 *
 * Persistence is dual, unlike the pure-browser-flag ruling of
 * `thinking/plans/2026_09_01_tos_gate.md` §8: `onAccept` is expected to both write the account
 * record (`POST /consent/accept`, `users.tosAcceptedAt`) and the browser flag
 * (`core/consent/storage.ts`, house 'ovid') — see `root.tsx`'s call site. The account row is what
 * stops a second device or a cleared browser from being asked again; the flag is the same-tab,
 * no-round-trip signal every other consent surface already uses.
 */
import {useEffect, useState} from 'react'
import {Link} from 'react-router'

export interface AgeTermsGateCopy {
    title: string
    ageLabel: string
    termsLabel: string
    termsLinkLabel: string
    privacyLinkLabel: string
    button: string
}

export interface AgeTermsGateProps {
    styles: Record<string, string>
    copy: AgeTermsGateCopy
    termsHref: string
    privacyHref: string
    /** Called once both boxes are ticked and Continue is pressed. The overlay closes as soon as
     *  this resolves (or throws — closing is the correct failure direction: the browser flag and
     *  local state are the fallback if the account stamp fails, and the account is re-asked next
     *  sign-in if neither lands). */
    onAccept: () => void | Promise<void>
}

export function AgeTermsGate({styles, copy, termsHref, privacyHref, onAccept}: AgeTermsGateProps) {
    const [ageChecked, setAgeChecked] = useState(false)
    const [termsChecked, setTermsChecked] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const canContinue = ageChecked && termsChecked && !submitting

    // The gate is the only interactive surface while it is up — same contract as AgeGate.
    useEffect(() => {
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = previousOverflow
        }
    }, [])

    async function handleContinue() {
        if (!canContinue) return
        setSubmitting(true)
        try {
            await onAccept()
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div
            className={styles.overlay}
            role="dialog"
            aria-modal="true"
            aria-labelledby="age-terms-gate-title"
        >
            <div className={styles.card}>
                <h2 id="age-terms-gate-title" className={styles.title}>{copy.title}</h2>
                <label className={styles.checkbox}>
                    <input
                        type="checkbox"
                        checked={ageChecked}
                        onChange={(e) => setAgeChecked(e.target.checked)}
                    />
                    <span>{copy.ageLabel}</span>
                </label>
                <label className={styles.checkbox}>
                    <input
                        type="checkbox"
                        checked={termsChecked}
                        onChange={(e) => setTermsChecked(e.target.checked)}
                    />
                    <span>{copy.termsLabel}</span>
                </label>
                <div className={styles.links}>
                    <Link to={termsHref} onClick={(e) => e.stopPropagation()}>{copy.termsLinkLabel}</Link>
                    <Link to={privacyHref} onClick={(e) => e.stopPropagation()}>{copy.privacyLinkLabel}</Link>
                </div>
                <button
                    type="button"
                    className={styles.button}
                    disabled={!canContinue}
                    onClick={handleContinue}
                >
                    {copy.button}
                </button>
            </div>
        </div>
    )
}

export default AgeTermsGate
